import React, { useState, useEffect } from 'react';
import TextEditor from '@components/form-elements/TextEditor';
import { fetchJson } from '@utils/fetchJson';
import { formatTimeAgo, formatExactDate } from '@utils/timeUtils';
import { API_BASE_URL, APP_BASE_URL } from '../../config';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useTruncatedHtml } from '@hooks/useTruncatedHtml';
import { Edit, Edit2, Pencil, Trash2 } from 'lucide-react';
import UserAvatar from '@uiElements/UserAvatar';
const CommentItem = ({ item,record_type, onUpdated }) => {
  const {
    id,
    body,
    name,
    photo,
    created_date,
    modified_date,
    user_id,
    created_by,
  } = item;
  const [hovered, setHovered] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const { isLong, truncatedHtml, fullHtml } = useTruncatedHtml(body, 150, '...');
  const [isEditing, setIsEditing] = useState(false);
  const [isMyComments, setisMyComments] = useState(false);
  const [editContent, setEditContent] = useState(body);

  const userData = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    const myComment = userData?.['adminID'] === created_by;
    setisMyComments(!!myComment);
  }, [userData, created_by]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    const res = await fetchJson(`${API_BASE_URL}/comments/delete`, {
      method: 'POST',
      body: JSON.stringify({
        list: id,
        action: 'changeStatus',
        status: 'delete',
        type:record_type,
      }),
    });
    if (res?.flag === 'S') onUpdated();
  };

  const handleEdit = async () => {
    const res = await fetchJson(`${API_BASE_URL}/comment/${id}`, {
      method: 'POST',
      body: JSON.stringify({
        id,
        body: editContent,
        status: 'active',
        mentions: [],
        record_id: item.record_id,
        type:record_type,
      }),
    });
    if (res?.flag === 'S') {
      setIsEditing(false);
      onUpdated();
    }
  };

  const avatar = photo
    ? `${APP_BASE_URL}/uploads/profilephoto/${user_id}/profilePic/${photo}`
    : null;

  return (
    <div
      className="w-full max-w-full px-4 py-2 rounded-md relative hover:bg-gray-50 overflow-x-hidden"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Edit/Delete Buttons */}
      {hovered && isMyComments && (
        <div className="absolute top-2 right-2 space-x-2 text-sm">
          <button onClick={() => setIsEditing(true)} className="text-blue-500 hover:text-blue-800">
            <Pencil size={18}/>
          </button>
          <button onClick={handleDelete} className="text-red-500 hover:text-red-800">
            <Trash2 size={18} />
          </button>
        </div>
      )}

      <div className="flex items-start gap-3">
        <UserAvatar
        className="mt-1 relative group"
          name={name}
          imageUrl={avatar}
          size={32}
        />
        {/* min-w-0 is critical so content can shrink inside narrow parents */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col items-start min-w-0">
            <span className="font-semibold text-gray-800 break-words">{name}</span>

            <span className="text-xs text-gray-500 flex items-center gap-1">
              {formatTimeAgo(modified_date || created_date)}

              {modified_date && (
                <Tooltip.Provider delayDuration={100}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <span className="ml-1 text-gray-400 text-[12px] cursor-default">edited</span>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        side="top"
                        className="bg-black text-white text-xs rounded px-2 py-1 shadow-md z-50"
                      >
                        Modified at {formatExactDate(modified_date)}
                        <Tooltip.Arrow className="fill-black" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              )}
            </span>
          </div>

          {isEditing ? (
            <>
              <div className="mt-2 bg-white">
                <TextEditor
                  value={editContent}
                  showEditorInitially={true}
                  onChange={setEditContent}
                />
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={handleEdit}
                  className="text-white bg-blue-600 px-3 py-1 rounded text-sm"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditContent(body);
                  }}
                  className="text-sm text-gray-600 hover:underline"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-700 mt-1">
              {/* Wrapper that enforces wrapping & hides horizontal overflow */}
              <div
                className="break-words whitespace-pre-wrap overflow-hidden"
                style={{
                  // extra safety for arbitrary HTML content
                  wordBreak: 'break-word',
                }}
              >
                <div
                  className="comment-html"
                  // NOTE: if your HTML can include images/tables, consider a CSS rule in your global stylesheet:
                  // .comment-html img{max-width:100%;height:auto} .comment-html table{display:block;max-width:100%;overflow:auto}
                  dangerouslySetInnerHTML={{ __html: showFull ? fullHtml : truncatedHtml }}
                />
              </div>

              {isLong && (
                <button
                  className="text-blue-500 mt-1 text-xs underline"
                  onClick={() => setShowFull(!showFull)}
                >
                  {showFull ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentItem;
